import type { FilterFieldDef, FilterSurface } from './types'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface ToggleChipFilterOptions {
    label: () => string
    /** Giá trị ghi vào URL khi bật. Mặc định `'yes'`, đúng tập giá trị mà
     *  một `selectFilter<'all' | 'yes'>` hai lựa chọn từng dùng, để đổi từ
     *  dropdown sang chip không đổi hình dạng URL. */
    onValue?: string
    /** Giá trị khi tắt / mặc định. Mặc định `'all'`. */
    offValue?: string
}

const sizeClass = (surface: FilterSurface) =>
    surface === 'sheet' ? 'h-11' : 'h-9 md:h-8'

/**
 * Bộ lọc có/không dạng chip bấm được (aria-pressed), thay cho dropdown hai
 * lựa chọn "Tất cả trạng thái" / giá trị — dropdown đó buộc người dùng đọc
 * nhãn rồi suy ra trạng thái, còn chip tự thân đã là trạng thái (bấm =
 * đang lọc, nhả = không lọc), cùng lúc vẫn giữ nguyên contract URL 'all'/'yes'
 * của `selectFilter` hai lựa chọn mà nó thay thế.
 */
export function toggleChipFilter({
    label,
    onValue = 'yes',
    offValue = 'all',
}: ToggleChipFilterOptions): FilterFieldDef<string> {
    return {
        defaultValue: offValue,
        label,
        parse: (raw) =>
            raw === onValue ? onValue : raw === offValue ? offValue : undefined,
        // "<label>: yes" đọc rất kỳ cho một bộ lọc có/không — chip chỉ cần
        // nhắc tên, giống toggleFilter.
        chipLabel: () => label(),
        render: ({ id, value, onChange, surface }) => {
            const pressed = value === onValue
            return (
                <Button
                    id={id}
                    type="button"
                    size="sm"
                    variant={pressed ? 'default' : 'outline'}
                    aria-pressed={pressed}
                    className={cn('w-full justify-center', sizeClass(surface))}
                    onClick={() => onChange(pressed ? offValue : onValue)}
                >
                    {label()}
                </Button>
            )
        },
    }
}
